// The commands every view of a pull request answers (the page around the
// views), and the app's own under every page.

import { stepText } from '../logic/prStack';
import { GH, historyPath, repoIndexPath, repoPath, subjectPath, UNDER_PARAM, type PrTab, type Route } from '../router';
import { routeRepo } from '../jump/interpret';
import { isNavPlace, times, type Command, type NavPlace, type Place } from './command';
import { leave, nothingFor, notApplicable, pass, type Update } from './effects';

export interface PageModel {
  tab: PrTab;
  /** Why nothing can be written here (the App does not cover the repository); null while it can. */
  readOnly: string | null;
  stack: PageStack;
  /** Its head branch's history (`pullHistoryPath`), once the pull request has loaded; null until then. */
  history: string | null;
}

/**
 * The pull request's stack, as the page knows it: still loading, failed to
 * load, none (it is in no stack), or the pull requests the stack motions
 * step through below it (towards the base) and above it, nearest first.
 */
export type PageStack = { is: 'loading' } | { is: 'failed' } | { is: 'none' } | { is: 'known'; below: readonly string[]; above: readonly string[] };

export type PageEffect =
  | { kind: 'navigate'; tab: PrTab }
  /** On the Review view already: put the focus on its publish button. */
  | { kind: 'focus-publish' }
  | { kind: 'scroll-window'; to: 'top' | 'end' }
  | { kind: 'find-file' }
  | { kind: 'show-guide' }
  /** Open the header's stack menu (which says why, when the stack could not be read). */
  | { kind: 'open-stack' }
  /** Go to this pull request of the stack, on the view shown. */
  | { kind: 'open-pr'; id: string }
  /** Say why nothing can be written, with the way to install the App. */
  | { kind: 'read-only' }
  /** Go to the page at `path` (its head branch's history). */
  | { kind: 'go'; path: string };

const TABS: Partial<Record<Place, PrTab>> = { overview: 'overview', files: 'files', commits: 'commits', review: 'review' };

/** What a stack command says where there is no stack to act on. */
const OUTSIDE_STACK = { none: 'This pull request is not part of a stack.', loading: 'This pull request’s stack is still loading.' } as const;

export const TAB_TITLE: Readonly<Record<PrTab, string>> = { overview: 'Overview', files: 'Files', commits: 'Snapshots', review: 'Review' };

/** What `command` does on a pull request's page, whichever view shows: the view above has already passed it on. */
export function updatePage(m: PageModel, command: Command): Update<PageModel, PageEffect> {
  switch (command.kind) {
    case 'goto': {
      if (command.place === 'top' || command.place === 'end') return [m, [{ kind: 'scroll-window', to: command.place }]];
      if (command.place === 'named-file') return [m, [{ kind: 'find-file' }]];
      // Only the page knows its head branch; the repository's pages are the app's (`goFrom`).
      if (command.place === 'history') return [m, [m.history ? { kind: 'go', path: m.history } : notApplicable(PULL_LOADING)]];
      if (isNavPlace(command.place)) return [m, [pass]];
      const tab = TABS[command.place]!;
      if (tab !== 'review') return [m, tab === m.tab ? [] : [{ kind: 'navigate', tab }]];
      if (m.tab === 'review') return [m, [{ kind: 'focus-publish' }]];
      // As the header's disabled publish button: say why instead of going to a page that can only fail.
      return [m, [m.readOnly !== null ? { kind: 'read-only' } : { kind: 'navigate', tab }]];
    }
    case 'open':
      if (command.menu === 'guide') return [m, [{ kind: 'show-guide' }]];
      if (command.menu === 'finder') return [m, [notApplicable('The file picker is in Files', { kind: 'goto', place: 'files' })]];
      if (command.menu === 'stack') return [m, [m.stack.is === 'failed' || m.stack.is === 'known' ? { kind: 'open-stack' } : notApplicable(OUTSIDE_STACK[m.stack.is])]];
      if (command.menu === 'base' || command.menu === 'head') return [m, [notApplicable('A pull request’s base and head are changed on GitHub')]];
      return [m, [pass]];
    case 'move': {
      if (command.object !== 'pr' || command.only) return [m, [nothingFor(command, `in ${TAB_TITLE[m.tab]}`)]];
      // A stack that could not be read: the menu says why.
      if (m.stack.is === 'failed') return [m, [{ kind: 'open-stack' }]];
      if (m.stack.is !== 'known') return [m, [notApplicable(OUTSIDE_STACK[m.stack.is])]];
      const way = command.dir > 0 ? m.stack.above : m.stack.below;
      if (!way.length) return [m, [notApplicable(stepText(command.dir > 0 ? 'above' : 'below'))]];
      return [m, [{ kind: 'open-pr', id: way[Math.min(times(command), way.length) - 1] }]];
    }
    case 'select':
    case 'range':
    case 'grow':
    case 'act':
    case 'fold':
    case 'present':
    case 'scroll':
      return [m, [nothingFor(command, `in ${TAB_TITLE[m.tab]}`)]];
    case 'refresh':
    case 'submit':
    case 'cancel':
      return [m, [pass]];
  }
}

const PULL_LOADING = 'The pull request is still loading';

/** A pull request's head, as its page has read it. */
export interface PullHead {
  owner: string;
  repo: string;
  headRefName: string;
  headRefOid: string;
  /** The repository the head branch lives in; null (or not read) once a fork is deleted. */
  headRepository?: { nameWithOwner: string } | null;
  /** The head branch; null once it is deleted. Not read: taken to be there. */
  headRef?: { name: string } | null;
}

/** Whether a pull request's head branch is gone: its repository (a deleted fork) or the branch itself (deleted on merge, say). */
export const headGone = (pr: PullHead) => !pr.headRepository || pr.headRef === null;

/**
 * A pull request's head branch's history, in the repository it lives in
 * (a fork's own); once the branch or its fork is gone (`headGone`), its
 * head commit's history in the pull request's repository, which keeps the
 * commit (`refs/pull/N/head`).
 */
export function pullHistoryPath(pr: PullHead): string {
  const [owner, repo] = pr.headRepository?.nameWithOwner.split('/') ?? [];
  if (owner && repo && pr.headRef !== null) return subjectPath({ kind: 'ref', owner, repo, ref: pr.headRefName, path: null });
  return subjectPath({ kind: 'ref', owner: pr.owner, repo: pr.repo, ref: pr.headRefOid, path: null });
}

/** Where a navigation key leads: a path, or why it leads nowhere from here. */
export type Go = { path: string } | { why: string };

const NO_REPO = 'This page is about no repository';

/**
 * Where `place` leads from the page at `route`: its repository's page or
 * Branches tab (on any page of a repository, `routeRepo`), the repository
 * index, or the history of the subject shown (a compare's head branch's,
 * in its fork; a history's own commits, from its Files view). A pull
 * request's history is its page's to say (`updatePage`): only the page
 * knows its head branch.
 */
export function goFrom(route: Route, place: NavPlace): Go {
  switch (place) {
    case 'repositories':
      return route.name === 'repos' ? { why: 'The repository index is the page shown' } : { path: repoIndexPath() };
    case 'repository':
    case 'branches': {
      const repo = routeRepo(route);
      if (!repo) return { why: NO_REPO };
      const tab = place === 'repository' ? 'pulls' : 'branches';
      if (route.name === 'repo' && route.tab === tab) return { why: tab === 'pulls' ? 'The repository page is the page shown' : 'Its Branches tab is the page shown' };
      return { path: repoPath(repo.owner, repo.repo, tab) };
    }
    case 'history': {
      if (route.name === 'compare') {
        const s = route.subject;
        // A head in the owner's fork GitHub finds (`main...ana:feat`) is read in the fork of the repository's name.
        const at = s.headRepo ? { owner: s.headRepo.owner, repo: s.headRepo.repo ?? s.repo } : s;
        return { path: subjectPath({ kind: 'ref', owner: at.owner, repo: at.repo, ref: s.head, path: null }) };
      }
      if (route.name === 'ref') {
        if (route.view === 'commits') return { why: 'This is the history' };
        // The locator as its URL named it, on its Commits view: the page resolves it as before.
        const l = route.locator;
        const path = historyPath(l.owner, l.repo, l.spec) ?? `${GH}/${l.owner}/${l.repo}/commits`;
        return { path: l.path === undefined ? path : `${path}?${new URLSearchParams({ [UNDER_PARAM]: l.path })}` };
      }
      if ('subject' in route) return { why: PULL_LOADING };
      return { why: 'Only a pull request, a compare or a history has a history to go to' };
    }
  }
}

export interface AppModel {
  signedIn: boolean;
  /** The Settings page is the one shown. */
  inSettings: boolean;
  /** The page shown, which the navigation keys lead from. */
  route: Route;
}

export type AppEffect = { kind: 'show-feedback' } | { kind: 'open-settings' } | { kind: 'show-jump' } | { kind: 'go'; path: string };

/**
 * The app's own commands, under every page's: feedback, Settings, the
 * jump bar and the pages one pivot away (`goFrom`), for someone signed in
 * (the key is left alone otherwise).
 */
export function updateApp(m: AppModel, command: Command): Update<AppModel, AppEffect> {
  const go = command.kind === 'goto' && isNavPlace(command.place);
  if (!go && (command.kind !== 'open' || (command.menu !== 'feedback' && command.menu !== 'settings' && command.menu !== 'jump'))) return [m, [pass]];
  if (!m.signedIn) return [m, [leave]];
  if (command.kind === 'goto') {
    const to = goFrom(m.route, command.place as NavPlace);
    return [m, ['path' in to ? { kind: 'go', path: to.path } : notApplicable(to.why)]];
  }
  if (command.kind !== 'open') return [m, [pass]];
  if (command.menu === 'feedback') return [m, [{ kind: 'show-feedback' }]];
  if (command.menu === 'jump') return [m, [{ kind: 'show-jump' }]];
  return [m, [m.inSettings ? notApplicable('Settings is the page shown') : { kind: 'open-settings' }]];
}

export type SettingsEffect = { kind: 'leave-settings' };

/** The Settings page's one command of its own: leaving it. */
export function updateSettingsPage<M>(m: M, command: Command): Update<M, SettingsEffect> {
  return [m, [command.kind === 'cancel' ? { kind: 'leave-settings' } : pass]];
}

export type SendEffect = { kind: 'publish' };

/** The Review view's one command of its own: sending the review. */
export function updateSend<M>(m: M, command: Command): Update<M, SendEffect> {
  return [m, [command.kind === 'submit' ? { kind: 'publish' } : pass]];
}

/**
 * A compare's page (views/CompareView.svelte) or a branch history's
 * (views/RefView.svelte): which of its views shows, and which of the two
 * it is (what its refusals call it).
 */
export interface BranchPageModel {
  view: 'files' | 'commits';
  noun: 'compare' | 'history';
}

export type BranchPageEffect =
  | { kind: 'navigate'; view: 'files' | 'commits' }
  | { kind: 'scroll-window'; to: 'top' | 'end' }
  | { kind: 'find-file' }
  | { kind: 'show-guide' }
  /** Open the header's picker of the base or the head (a history's one picker, its ref's, is its base). */
  | { kind: 'pick'; end: 'base' | 'head' };

const BRANCH_VIEW_TITLE = { files: 'Files', commits: 'Commits' } as const;
const BRANCH_NOUN = { compare: 'A compare', history: 'A branch history' } as const;

/**
 * What `command` does on a compare's or a branch history's page, whichever
 * view shows: its two views (Files, and Commits where the pull request's
 * page has Snapshots), the window's ends, Find file, the key guide and the
 * header's pickers (a compare's base and head, a history's ref). A pull
 * request's other views and its stack are not theirs, and say so.
 */
export function updateBranchPage(m: BranchPageModel, command: Command): Update<BranchPageModel, BranchPageEffect> {
  switch (command.kind) {
    case 'goto': {
      if (command.place === 'top' || command.place === 'end') return [m, [{ kind: 'scroll-window', to: command.place }]];
      if (command.place === 'named-file') return [m, [{ kind: 'find-file' }]];
      if (command.place === 'files' || command.place === 'commits') return [m, command.place === m.view ? [] : [{ kind: 'navigate', view: command.place }]];
      // The pages one pivot away are the app's (`goFrom`).
      if (isNavPlace(command.place)) return [m, [pass]];
      return [m, [notApplicable(`${BRANCH_NOUN[m.noun]} has its files and its commits only`)]];
    }
    case 'open':
      if (command.menu === 'guide') return [m, [{ kind: 'show-guide' }]];
      if (command.menu === 'stack') return [m, [notApplicable(`${BRANCH_NOUN[m.noun]} is not part of a stack.`)]];
      if (command.menu === 'finder') return [m, [notApplicable('The file picker is in Files', { kind: 'goto', place: 'files' })]];
      if (command.menu === 'base') return [m, [{ kind: 'pick', end: 'base' }]];
      if (command.menu === 'head') return [m, [m.noun === 'compare' ? { kind: 'pick', end: 'head' } : notApplicable('A branch history has one ref to pick', { kind: 'open', menu: 'base' })]];
      return [m, [pass]];
    case 'move':
    case 'select':
    case 'range':
    case 'grow':
    case 'act':
    case 'fold':
    case 'present':
    case 'scroll':
      return [m, [nothingFor(command, `in ${BRANCH_VIEW_TITLE[m.view]}`)]];
    case 'refresh':
    case 'submit':
    case 'cancel':
      return [m, [pass]];
  }
}

/** The draft verbs: a pull request's drafts are no compare's or history's. */
const DRAFT_VERBS: ReadonlySet<string> = new Set(['comment', 'edit', 'discard', 'include']);

/**
 * Whether a compare's or a branch history's page has anything `command`
 * could act on, for its key guide: not a pull request's other views, its
 * stack, or its drafts and comments (the Files view refuses those there,
 * saying why).
 */
export function branchOffers(command: Command): boolean {
  if (command.kind === 'goto') return ['files', 'commits', 'top', 'end', 'named-file'].includes(command.place) || isNavPlace(command.place);
  if (command.kind === 'open') return command.menu !== 'stack';
  if (command.kind === 'move') return command.object !== 'pr';
  if (command.kind === 'act') return !DRAFT_VERBS.has(command.verb);
  return true;
}
